import {
  ApiResponse,
  IAuthDetails,
  IEmailVerificationRequest,
  ILoginRequest,
  IUser,
} from '@/types';
import { trialOnboardingContext } from '@/utils/trialCheckout';

import http, { HTTPOptions } from '../http';

export const getLoggedInUser = async (options?: HTTPOptions) =>
  http.get<ApiResponse<{ user: IUser }>>('/auth/me', options);

// Sign-in requests are public: they are sent without the stored session, so an expired or
// unreadable cookie left from an earlier session can neither block them locally nor trigger
// a refresh first.
export const loginWithEmail = async (payload: ILoginRequest) =>
  http.post<ApiResponse<null>>(
    '/auth/login/email',
    { ...payload, ...trialOnboardingContext() },
    { auth: false }
  );

export const googleSignIn = async (query: { id_token: string }) => {
  const queryParams = new URLSearchParams({
    id_token: query.id_token,
    ...trialOnboardingContext(),
  });

  return http.get<ApiResponse<IAuthDetails>>(`/auth/google?${queryParams.toString()}`, {
    auth: false,
  });
};

export const verifyEmail = async (payload: IEmailVerificationRequest) =>
  http.post<ApiResponse<IAuthDetails>>('/auth/verify/email', payload, { auth: false });

export const resendEmailVerification = async (email: string) =>
  http.post<ApiResponse<null>>('/auth/verify/email/resend', { email }, { auth: false });

// Device authorization ("agent login")
export const getDeviceAuthorizationRequest = async (userCode: string) =>
  http.get<ApiResponse<any>>(`/auth/device/requests/${encodeURIComponent(userCode)}`);

export const approveDeviceAuthorizationRequest = async (userCode: string) =>
  http.post<ApiResponse<any>>(`/auth/device/requests/${encodeURIComponent(userCode)}/approve`, {});

export const denyDeviceAuthorizationRequest = async (userCode: string) =>
  http.post<ApiResponse<any>>(`/auth/device/requests/${encodeURIComponent(userCode)}/deny`, {});

// Private Bot Authentication
export const sendPrivateBotOtp = async (
  payload: { email: string; chatbotId: string },
  apiEndpoint?: string
) => {
  const endpoint = apiEndpoint || process.env.NEXT_PUBLIC_API_ENDPOINT;
  return http.post<ApiResponse<null>>(
    '/auth/private-bot/send-otp',
    payload,
    endpoint ? { baseURL: endpoint } : undefined
  );
};

export const verifyPrivateBotOtp = async (
  payload: { email: string; otp: string; chatbotId: string },
  apiEndpoint?: string
) => {
  const endpoint = apiEndpoint || process.env.NEXT_PUBLIC_API_ENDPOINT;
  return http.post<ApiResponse<{ sessionToken: string }>>(
    '/auth/private-bot/verify-otp',
    payload,
    endpoint ? { baseURL: endpoint } : undefined
  );
};
